import React, { useState, useRef, useEffect } from 'react';
import Icon, { IconName } from './icons';
import Tooltip from './tooltip';
import { checkActiveUrl } from '../../utils/browserUtils';

interface DropdownProps {
  id: string;
  isMobile?: boolean;
  options: Array<any>;
  clickHandler: Function;
  iconName?: IconName;
  selected: Record<string, any> | null;
  disabled: boolean;
  toolTipMessage: string;
  setShowDropdownState?: Function;
  color?: any;
  dropdownName?: any;
}

export default function Dropdown(props: Readonly<DropdownProps>) {
  const {
    id,
    isMobile,
    options,
    clickHandler,
    iconName,
    selected,
    disabled,
    toolTipMessage,
    setShowDropdownState,
    color,
    dropdownName
  } = props;
  const [showDropdown, setShowDropdown] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const url = new URL(window.location.href);
  const [relativepath, setRelativePath] = useState<string>('');
  const [focusedIndex, setFocusedIndex] = useState(-1);
  useEffect(() => {
    if (setShowDropdownState) {
      setShowDropdownState(showDropdown || showTooltip)
    }
  }, [showDropdown, showTooltip])

  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setRelativePath(url.pathname);
  }, [url]);

  // Toggle dropdown visibility
  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
    setFocusedIndex(-1); // Reset focus
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
        setFocusedIndex(-1);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const handleKeyDown = (event: any) => {
    
    if (event.key === "Tab" && event.shiftKey && showDropdown) {
      setFocusedIndex((prevIndex) =>
        prevIndex <= 0 ? options.length - 1 : (prevIndex - 1) % options.length
      );

    }else if (event.key === "Tab" && showDropdown) {
      
      setFocusedIndex((prevIndex) => (prevIndex + 1) % options.length);
      if((options.length-1) === focusedIndex){
        setShowDropdown(false);
        setFocusedIndex(-1);
      }
    } else if (event.key === "Enter" && showDropdown) {
      event.preventDefault();
      if (focusedIndex !== -1) {
        handleItemClick(options[focusedIndex]);
      }
    } else if (event.key === "Escape") {
      setShowDropdown(false);
      setFocusedIndex(-1);
    }
  };

  // Handle item selection
  const handleItemClick = (item: any) => {
    setShowDropdown(false); // Close the menu
    setFocusedIndex(-1); // Reset focus
    clickHandler(item.key);
  };



  return (
    <div className="flex justify-center" ref={dropdownRef} onKeyDown={handleKeyDown}  >
      <div className={`${isMobile ? "w-full" : ""}`}>

        <div className="relative inline-block outline-none w-full">
          <button className={`
          relative text-base leading-tight transition duration-150 ease-in-out
          flex items-center whitespace-nowrap
          ${color ? color : 'text-secondary-300'}
          ${isMobile ? "flex justify-between w-full" : "mx-sm"}
        `}
            type="button"
            id={id}
            data-testid={id}
            aria-expanded={showDropdown}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            onClick={toggleDropdown}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                setShowTooltip(!showTooltip);
              }else if (e.key === "Tab" && showTooltip) {
                setShowTooltip(false);
              }
            }}
            // dataAnalytics={buildAnalytics(DataAnalyticsLocations.Header, DataAnalyticsTypes.Button)}
            role='button'
            name={dropdownName}
            aria-controls={id}
            aria-haspopup="true"
            // aria-label={`${selected?.value} ${dropdownName}`}
            aria-label={ disabled && showTooltip ? toolTipMessage : `${selected?.value}`}
          >
            {disabled && showTooltip && (
              <Tooltip isMobile={isMobile} showTooltip={showTooltip} text={toolTipMessage} />
            )}
            <div className={`flex items-center ${isMobile ? "w-full" : ""}`}>
              {/* {iconName ? <Icon name={iconName} className="text-xl" /> : null} */}
              <div className={`flex items-center border py-2 px-4 rounded-md ${isMobile ? "w-full" : ""}`}>
                <span className={`mr-1 px-1 flex items-center ${isMobile ? "w-11/12 text-md" : "text-sm"}`}>
                  {selected?.iconName && <Icon name={selected?.iconName} className="pr-2 w-full" />}
                  {selected?.value} 
                </span>
                <Icon name="downArrow" className={`text-xl ${color} justify-end`} />

              </div>
            </div>
          </button>
          {showDropdown && !disabled && (
            <div className="absolute z-[9999] w-full h-[420px]">
              <ul className={`block
                  min-w-full
                  bg-white
                  float-left
                  list-none
                  text-left
                  mt-1
                  m-0
                  drop-shadow-md
                  bg-clip-padding
                  ${isMobile ? "border" : "border-none"}
                  ${showDropdown && !disabled ? "" : "hidden"}
                    `}
                aria-labelledby="dropdownMenuButton"
              >

                {options && options.map((item, index) => {
                  return (

                    <li
                      key={`${item.key}_${index}`}
                      data-testid={`${item.key}_${index}`}
                      tabIndex={-1}
                      className={`w-full text-card-header hover:text-white hover:bg-secondary-400 
                           ${focusedIndex === index ? "bg-secondary-400 text-white" : ""}
                          ${checkActiveUrl(item.key, relativepath) ? 'bg-secondary-400 text-white ' : ''}
                        `}
                      data-name={item.value}
                      data-alt-text={item.value}
                      data-selected={selected?.key === item.key}
                    >
                      {/* <div className="w-2/12 inline-flex justify-center align-middle">
                        {selected?.key === item.key && !item?.iconName ? (
                          <Icon name="check" className="text-2xl" />
                        ) : null}
                        {item?.iconName && <Icon name={item?.iconName} className="text-xl pl-1" />}
                      </div> */}
                      <a
                        className="
                        font-small
                        text-base
                        font-medium
                        py-2
                        px-3
                        w-full
                        whitespace-nowrap
                        bg-transparent
                        flex
                        justify-start 
                        "
                        href='#'
                        aria-posinset={index + 1}
                        aria-setsize={options.length}
                        onClick={(e) => {
                          e.preventDefault();
                          handleItemClick(item);
                        }}
                      //  aria-label={`${item.value}, ${index + 1} of ${options.length} ${dropdownName}`}
                      >
                        <div className="w-2/12 inline-flex justify-center align-middle mr-2">
                          {selected?.key === item.key && !item?.iconName ? (
                            <Icon name="check" className="text-2xl" />
                          ) : null}
                          {item?.iconName && <Icon name={item?.iconName} className="text-xl" />}
                        </div>
                        <span className='flex text-sm'>{item.value}</span>
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
